// src/components/principal/PrincipalSetupWizard.jsx

import React, { useEffect, useState } from "react";
import api from "../../services/api";
import MobileAppOnboardingCard from "../MobileAppOnboardingCard";

const PrincipalSetupWizard = ({
  profile,
  onProfileChange,
  onError,
  initialStep = 1,
  onComplete,
  visible = true,
}) => {
  const [step, setStep] = useState(initialStep || 1);
  const [displayName, setDisplayName] = useState(
    profile.displayName || (profile.email ? profile.email.split("@")[0] : "")
  );
  const [schoolHashtag, setSchoolHashtag] = useState(
    profile.schoolHashtag || ""
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setStep(initialStep || 1);
  }, [initialStep]);

  useEffect(() => {
    setDisplayName(
      profile.displayName || (profile.email ? profile.email.split("@")[0] : "")
    );
    setSchoolHashtag(profile.schoolHashtag || "");
  }, [profile.displayName, profile.schoolHashtag, profile.email]);

  if (!visible) return null;

  // Friendly name for greetings
  const prettyName =
    profile.principalName ||
    profile.displayName ||
    (profile.email ? profile.email.split("@")[0] : "there");

  // Derive "Principal [SecondName]" example from prettyName
  const rawName = (prettyName || "").trim();
  let surnameExample = "Murphy";
  if (rawName) {
    const parts = rawName.split(/\s+/);
    surnameExample = parts.length > 1 ? parts[parts.length - 1] : parts[0];
  }

  // Use real school name where possible
  const schoolNameExample = profile.schoolName || "Your School";

  // Derive a nice hashtag suggestion from the school name, e.g. "Shanagolden NS" -> "#ShanagoldenNS"
  const rawSchoolName = (profile.schoolName || "").trim();
  let schoolHashtagExample = "#YourSchoolName";
  if (rawSchoolName) {
    const noPunct = rawSchoolName.replace(/[^A-Za-z0-9\s]+/g, "");
    const camel = noPunct
      .split(/\s+/)
      .filter(Boolean)
      .map(
        (w) =>
          w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
      )
      .join("");
    if (camel) {
      schoolHashtagExample = `#${camel}`;
    }
  }

  const normaliseHashtag = (value) => {
    let raw = value || "";
    raw = raw.trim().replace(/\s+/g, "_");
    if (!raw) return "";
    if (!raw.startsWith("#")) {
      raw = "#" + raw;
    }
    return raw;
  };

  const handleSaveDisplayName = async (e) => {
    e?.preventDefault && e.preventDefault();
    const trimmed = (displayName || "").trim();
    if (!trimmed) {
      onError?.("Display name cannot be empty.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/update-display-name", {
        new_display_name: trimmed,
      });
      const updatedProfile = { ...profile, displayName: trimmed };
      onProfileChange?.(updatedProfile);
      setStep(2);
    } catch (err) {
      onError?.(
        err?.response?.data?.detail || "Failed to update display name."
      );
    } finally {
      setBusy(false);
    }
  };

  const handleHashtagInput = (e) => {
    const value = e.target.value;
    setSchoolHashtag(value);
  };

  const handleSaveHashtag = async (e) => {
    e?.preventDefault && e.preventDefault();
    const normalised = normaliseHashtag(schoolHashtag);
    if (!normalised) {
      onError?.("School hashtag cannot be empty.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/update-school-hashtag", {
        new_hashtag: normalised,
      });
      const updatedProfile = { ...profile, schoolHashtag: normalised };
      onProfileChange?.(updatedProfile);
      setSchoolHashtag(normalised);
      setStep(3);
    } catch (err) {
      onError?.(
        err?.response?.data?.detail || "Failed to update school hashtag."
      );
    } finally {
      setBusy(false);
    }
  };

  const handleAppDownloaded = () => {
    onComplete?.();
  };

  return (
    <div className="principal-setup-overlay">
      <div className="principal-setup-modal">
        <div className="principal-setup-header">
          <p className="principal-setup-kicker">First-time setup</p>
          <h2 className="principal-setup-title">
            Welcome to GhrianGhraffy, {prettyName}!
          </h2>
          <p className="principal-setup-subtitle">
            Just three quick steps and your school will be ready to start
            sharing photos with families.
          </p>
        </div>

        <ol className="principal-setup-steps">
          <li
            className={`principal-setup-step ${
              step === 1 ? "is-active" : step > 1 ? "is-complete" : ""
            }`}
          >
            <span className="principal-setup-step-number">1</span>
            <span className="principal-setup-step-text">
              Choose your display name
            </span>
          </li>
          <li
            className={`principal-setup-step ${
              step === 2 ? "is-active" : step > 2 ? "is-complete" : ""
            }`}
          >
            <span className="principal-setup-step-number">2</span>
            <span className="principal-setup-step-text">
              Set your school hashtag
            </span>
          </li>
          <li
            className={`principal-setup-step ${
              step === 3 ? "is-active" : ""
            }`}
          >
            <span className="principal-setup-step-number">3</span>
            <span className="principal-setup-step-text">
              Install the mobile app
            </span>
          </li>
        </ol>

        {step === 1 && (
          <form
            className="principal-setup-panel"
            onSubmit={handleSaveDisplayName}
          >
            <h3>Choose how your name appears to staff and parents</h3>
            <p className="principal-setup-panel-text">
              This is the name we’ll show beside updates and inside your
              dashboard. Use a friendly name such as{" "}
              <strong>“Principal {surnameExample}”</strong> or{" "}
              <strong>“{schoolNameExample} Principal”</strong>.
            </p>

            <label className="principal-setup-label">
              Display name
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={255}
                placeholder={`e.g. Principal ${surnameExample}`}
                className="principal-setup-input"
              />
            </label>

            <div className="principal-setup-actions">
              <button
                type="submit"
                className="principal-setup-primary-btn"
                disabled={busy}
              >
                {busy ? "Saving…" : "Save and continue"}
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form className="principal-setup-panel" onSubmit={handleSaveHashtag}>
            <h3>Create your school hashtag</h3>
            <p className="principal-setup-panel-text">
              We use this hashtag to group photos for your school across the app
              and on your website. Keep it short and unique, like{" "}
              <strong>{schoolHashtagExample}</strong>.
            </p>

            <label className="principal-setup-label">
              School hashtag
              <input
                type="text"
                value={schoolHashtag}
                onChange={handleHashtagInput}
                maxLength={50}
                placeholder={schoolHashtagExample}
                className="principal-setup-input"
              />
              <span className="principal-setup-hint">
                Spaces become underscores and we’ll add the <code>#</code> for
                you if you forget.
              </span>
            </label>

            <div className="principal-setup-actions">
              <button
                type="button"
                className="principal-setup-secondary-btn"
                onClick={() => setStep(1)}
                disabled={busy}
              >
                Back
              </button>
              <button
                type="submit"
                className="principal-setup-primary-btn"
                disabled={busy}
              >
                {busy ? "Saving…" : "Save and continue"}
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <div className="principal-setup-panel">
            <h3>Install the GhrianGhraffy app</h3>
            <p className="principal-setup-panel-text">
              This is where you and your staff will actually take and upload
              photos. Tap the button on your phone to install the app, then you
              can share it with your staff.
            </p>

            <MobileAppOnboardingCard
              variant="principal"
              storageKey={null}
              dismissible={false}
              onDownload={handleAppDownloaded}
            />

            <div className="principal-setup-actions principal-setup-actions--final">
              <p className="principal-setup-panel-text small">
                Once you’ve tapped the download button on your phone, you’re all
                set — this setup screen won’t appear again.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrincipalSetupWizard;

